<template>
  <app-modal
    :model-value="isOpen"
    :size="dialogSize"
    @cancel="onCancel"
    class="smart-import-modal"
    :fullscreen="currentStep !== 'upload'"
  >
    <template #title>
      <span>
        {{ $t(`${TRANSLATION_KEY_PREFIX}.title`) }}
      </span>
    </template>

    <template #body>
      <template v-if="currentStep === 'upload'">
        <p class="text--medium text-text-primary font-weight-medium mb-2">
          {{ $t(`${TRANSLATION_KEY_PREFIX}.subtitle`) }}
        </p>
        <p class="text-text-secondary">
          {{ $t(`${TRANSLATION_KEY_PREFIX}.description`) }}
        </p>

        <div class="my-4">
          <AppDroppableArea
            class="smart-import-dropzone text-center d-flex flex-column justify-center align-center"
            :class="{'drag-in-progress': dragInProgress, 'disabled': selectedFile || importing}"
            :disabled="!!selectedFile || importing"
            @dragover="dragInProgress = true"
            @dragenter="dragInProgress = true"
            @dragleave="dragInProgress = false"
            @ondrop="onFileDrop"
          >
            <template v-if="importing">
              <v-progress-circular
                indeterminate
                size="24"
              />
              <p class="mt-4 font-weight-medium text-grey-800">
                {{ $t(`${TRANSLATION_KEY_PREFIX}.importing`) }}
              </p>
            </template>
            <template v-else>
              <div class="d-flex flex-column row-gap-4 justify-center align-center">
                <app-material-icon
                  name="upload"
                  size="lg"
                  :color="dragInProgress ? 'icon-info-bold' : (selectedFile ? 'icon-disabled' : 'icon-primary')"
                />

                <input
                  ref="fileInput"
                  class="d-none"
                  data-locator="word-file-input"
                  type="file"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  @change="onFileSelected"
                />

                <span :class="selectedFile ? 'text-text-disabled' : 'text-text-secondary'">
                  {{ $t(`${TRANSLATION_KEY_PREFIX}.dragAndDrop`) }}
                </span>

                <app-button
                  data-locator="import-from-computer-button"
                  type="outlined"
                  :disabled="!!selectedFile"
                  @click="onBrowseClick"
                  variant="neutral"
                >
                  {{ $t(`${TRANSLATION_KEY_PREFIX}.importFromComputer`) }}
                </app-button>
              </div>
            </template>
          </AppDroppableArea>

          <div v-if="uploadedFileId && selectedFile && !importing" class="mt-4 pa-4 d-flex align-center selected-file-card">
            <div>
              <OfficeIcon
                document-type="word"
                icon-type="document"
                class="mr-2"
                :width="32"
                :height="32"
              />
            </div>
            <div class="flex-grow-1">
              <div class="text-body-1 font-weight-regular text-text-secondary">{{ selectedFile.name }}</div>
              <app-filesize-display class="text-body-2 text-text-tertiary" :value="selectedFile.size" />
            </div>
            <app-button
              data-locator="clear-file-button"
              type="icon"
              size="small"
              class="ml-auto"
              @click="clearFile"
            >
              <app-material-icon name="close" />
            </app-button>
          </div>
        </div>
      </template>

      <SmartImportPreview
        v-else-if="currentStep === 'preview' && uploadedFileId"
        :file-id="uploadedFileId"
        :file-name="selectedFile?.name || ''"
        :preview="preview"
        @upvote="handleUpvote"
        @downvote="handleDownvote"
      />

      <SmartImportOptions
        v-else-if="currentStep === 'options'"
        v-model="importMode"
      />
    </template>

    <template #footer>
      <app-button
        v-if="currentStep === 'options'"
        data-locator="previous-button"
        type="outlined"
        variant="neutral"
        @click="onPrevious"
      >
        <template #left-icon>
          <app-material-icon name="arrow_back" color="inherit" />
        </template>
        {{ $t('common.previous') }}
      </app-button>

      <div class="ml-auto d-flex">
        <div v-if="currentStep === 'preview'" class="d-flex align-center column-gap-2 mr-6">
          <span class="text-no-wrap">{{ $t('project.checklist.dialogs.SmartImportPreview.feedbackQuestion') }}</span>
          <app-button
            data-locator="positive-feedback-button"
            :class="{ 'thumb-up-button': true, active: isSmartImportUpvoted }"
            type="icon"
            @click="handleUpvote"
          >
            <app-material-icon name="thumb_up" class="text-grey-600" :filled="isSmartImportUpvoted" />
          </app-button>
          <app-button
            data-locator="negative-feedback-button"
            :class="{ 'thumb-down-button': true, active: isSmartImportDownvoted }"
            type="icon"
            @click="handleDownvote"
          >
            <app-material-icon name="thumb_down" class="text-grey-600" :filled="isSmartImportDownvoted" />
          </app-button>
        </div>

        <app-button
          data-locator="cancel-button"
          class="mr-4"
          type="outlined"
          variant="neutral"
          @click="onClose"
        >
          {{ $t('common.cancel') }}
        </app-button>

        <app-button
          v-if="currentStep !== 'options'"
          data-locator="next-button"
          type="primary"
          variant="brand"
          :disabled="isNextButtonDisabled"
          @click="onNext"
        >
          {{ $t('common.next') }}
          <template #right-icon>
            <app-material-icon name="arrow_forward" color="inherit" />
          </template>
        </app-button>

        <app-button
          v-if="currentStep === 'options'"
          data-locator="confirm-import-button"
          type="primary"
          variant="brand"
          :loading="confirmingImport"
          :disabled="confirmingImport"
          @click="onConfirmImport"
        >
          {{ $t('project.checklist.dialogs.SmartImportModal.importChecklist') }}
        </app-button>
      </div>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex'

import AppDroppableArea from '@/common/AppDroppableArea.vue'
import OfficeIcon from '@/common/office/OfficeIcon.vue'
import { track } from '@/common/pendo/agent'
import { toast } from '@/common/utils/toast'
import type { AppModalSize } from '@/design-system/modals/AppModal.vue'
import SmartImportOptions from '@/project/checklist/dialogs/SmartImportOptions.vue'
import SmartImportPreview from '@/project/checklist/dialogs/SmartImportPreview.vue'
import checklistService from '@/services/checklist.service'
import { IMPORT_CHECKLIST_FROM_WORD, GET_CHECKLIST } from '@/store/modules/checklist/action_types'
import { SET_SMART_IMPORT_MODAL_IS_OPEN } from '@/store/modules/checklist/mutation_types'
import { UPLOAD_HIDDEN_FILE } from '@/store/modules/documents/action_types'

const TRANSLATION_KEY_PREFIX = 'project.checklist.dialogs.SmartImportModal'

export default defineComponent({
  name: 'SmartImportModal',
  components: {
    AppDroppableArea,
    OfficeIcon,
    SmartImportPreview,
    SmartImportOptions,
  },
  data() {
    return {
      TRANSLATION_KEY_PREFIX,
      currentStep: 'upload' as 'upload' | 'preview' | 'options',
      dragInProgress: false,
      selectedFile: null as File | null,
      importing: false,
      isFileDialogOpen: false,
      uploadedFileId: null as number | null,
      draftId: null as number | null,
      preview: null as any,
      importMode: 'replace' as 'replace' | 'append',
      isSmartImportUpvoted: false,
      isSmartImportDownvoted: false,
      confirmingImport: false,
    }
  },
  computed: {
    ...mapState('checklist', {
      isOpen: 'smartImportModalIsOpen',
      importChecklistFromWordPending: 'importChecklistFromWordPending',
    }),
    ...mapGetters('room', ['roomMnemo']),
    dialogSize(): AppModalSize {
      return this.currentStep === 'preview' || this.currentStep === 'options' ? 'xl' : 'lg'
    },
    hasPreviewError(): boolean {
      return !this.preview || !Array.isArray(this.preview) || this.preview.length === 0
    },
    isNextButtonDisabled(): boolean {
      if (!this.uploadedFileId || this.importing) {
        return true
      }
      if (this.currentStep === 'preview' && (this.importChecklistFromWordPending || this.hasPreviewError)) {
        return true
      }
      return false
    },
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.resetState()
      }
    },
  },
  methods: {
    ...mapMutations('checklist', [SET_SMART_IMPORT_MODAL_IS_OPEN]),
    ...mapActions('checklist', [IMPORT_CHECKLIST_FROM_WORD, GET_CHECKLIST]),
    ...mapActions('documents', [UPLOAD_HIDDEN_FILE]),
    ...mapActions('checklist', [GET_CHECKLIST]),
    resetState() {
      this.currentStep = 'upload'
      this.selectedFile = null
      this.importing = false
      this.dragInProgress = false
      this.isFileDialogOpen = false
      this.uploadedFileId = null
      this.draftId = null
      this.preview = null
      this.isSmartImportUpvoted = false
      this.isSmartImportDownvoted = false
      this.confirmingImport = false
    },
    onBrowseClick() {
      this.isFileDialogOpen = true

      const fileInput = this.$refs['fileInput'] as HTMLInputElement

      const onWindowFocus = () => {
        setTimeout(() => {
          this.isFileDialogOpen = false
        }, 100)
      }

      window.addEventListener('focus', onWindowFocus, { once: true })

      fileInput.click()
    },
    onFileSelected(event: Event) {
      this.isFileDialogOpen = false
      const target = event.target as HTMLInputElement
      const files = target.files
      if (files && files.length > 0) {
        this.handleFile(files[0])
      }
      target.value = ''
    },
    async onFileDrop(event: DragEvent) {
      this.dragInProgress = false
      const files = event.dataTransfer?.files
      if (files && files.length > 0) {
        this.handleFile(files[0])
      }
    },
    async handleFile(file: File) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ]
      const isValidExtension = file.name.endsWith('.docx')

      if (!validTypes.includes(file.type) && !isValidExtension) {
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.errorInvalidFile`))
        return
      }

      this.selectedFile = file
      this.importing = true

      const fileToUpload: any = {
        name: file.name,
        objectToPost: {
          fileRaw: file,
          fileName: file.name,
          title: file.name,
        },
        extension: file.name.split('.').pop(),
        progress: 0,
        status: 'queued',
      }

      try {
        await this.UPLOAD_HIDDEN_FILE({
          file: fileToUpload,
          mnemo: this.roomMnemo,
        })

        this.uploadedFileId = fileToUpload.id
        track('checklist_import_uploaded', { type: 'word', project_mnemo: this.roomMnemo, document_id: this.uploadedFileId })
      } catch (err) {
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.errorUpload`))
        console.error('Upload error:', err)
        this.selectedFile = null
      } finally {
        this.importing = false
      }
    },
    clearFile() {
      this.selectedFile = null
      this.uploadedFileId = null
    },
    onCancel() {
      if (this.isFileDialogOpen) {
        return
      }
      this.onClose()
    },
    onClose() {
      this.SET_SMART_IMPORT_MODAL_IS_OPEN(false)
    },
    async onNext() {
      if (!this.uploadedFileId) {
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.errorNoFile`))
        return
      }

      if (this.currentStep === 'upload') {
        this.currentStep = 'preview'
        try {
          const response = await this.IMPORT_CHECKLIST_FROM_WORD({
            mnemo: this.roomMnemo,
            data: { fileId: this.uploadedFileId },
          })

          this.draftId = response.draftId
          this.preview = response.preview
        } catch (error) {
          console.error('Import error:', error)
        }
      } else if (this.currentStep === 'preview') {
        this.currentStep = 'options'
      }
    },

    onPrevious() {
      if (this.currentStep === 'options') {
        this.currentStep = 'preview'
      } else if (this.currentStep === 'preview') {
        this.currentStep = 'upload'
      }
    },

    async onConfirmImport() {
      if (!this.draftId) {
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.errorNoFile`))
        return
      }
      this.confirmingImport = true
      try {
        await checklistService.importChecklistFromWordConfirm(this.roomMnemo, {
          draftId: this.draftId,
          reset: this.importMode === 'replace',
        })

        track('checklist_import_completed', { type: 'word', project_mnemo: this.roomMnemo, document_id: this.uploadedFileId })
        await this.GET_CHECKLIST(this.roomMnemo)
        toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.importSuccess`))
        this.onClose()
      } catch (error) {
        console.error('Import error:', error)
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.errorImport`))
      } finally {
        this.confirmingImport = false
      }
    },

    handleUpvote(): void {
      if (this.isSmartImportUpvoted) {
        this.isSmartImportUpvoted = false
      } else {
        this.isSmartImportUpvoted = true
        this.isSmartImportDownvoted = false
      }
    },

    handleDownvote(): void {
      if (this.isSmartImportDownvoted) {
        this.isSmartImportDownvoted = false
      } else {
        this.isSmartImportDownvoted = true
        this.isSmartImportUpvoted = false
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.smart-import-dropzone {
  border: 1px dashed rgb(var(--v-theme-border-primary));
  border-radius: 12px;
  background-color: rgb(var(--v-theme-surface-secondary));
  transition: all 0.2s ease;
  min-height: 300px;
  cursor: pointer;

  &.drag-in-progress {
    background-color: rgb(var(--v-theme-surface-info)) !important;
    outline: 1px dashed rgb(var(--v-theme-border-info-bold)) !important;
    border: none !important;
  }

  &.disabled {
    cursor: not-allowed;
    pointer-events: none;
  }
}

.selected-file-card {
  border: 1px solid rgb(var(--v-theme-border-primary));
  border-radius: 8px;
  background-color: rgb(var(--v-theme-surface-secondary));
}

</style>
